import { lazy, Suspense } from "react"
import { Route, Routes } from "react-router-dom"
import { useSelector } from "react-redux"
import Layout from "../Layout/Layout"
import ProtectedRoute from "./ProtectedRoute"
import Unauthorized from "./UnAuthorized"
import PageNotFound from "../Screens/PageNotFound/PageNotFound"
import PageSkeleton from "../components/PageSkeleton/PageSkeleton"
import AppManagement from "../Screens/ITSM/AppManagement/AppManagement"
import OfflineDevices from "../Screens/DeviceStatus/OfflineDevices"
import AwayDevices from "../Screens/DeviceStatus/AwayDevices"
import OnlineDevices from "../Screens/DeviceStatus/OnlineDevices"
import GroupDevices from "../Screens/Devices/GroupDevices/GroupDevices"
import GroupDevicesList from "../Screens/Devices/GroupDevices/GroupDevicesList"
import PatchManagement from "../Screens/ITSM/PatchManagement/PatchManagement"
import GlobalPatchManagement from "../Screens/ITSM/GlobalUpdates/GlobalPatchManagement"
import GlobalSoftwareManagement from "../Screens/ITSM/GlobalUpdates/GlobalSoftwareManagement"
import DeletedDevices from "../Screens/DeletedDevices/DeletedDevices"
import ViewLogs from "../Screens/Logs/ViewLogs"
import CompliantDevices from "../Screens/ComplianceStatus/CompliantDevices"
import NonCompliantDevices from "../Screens/ComplianceStatus/NonCompliantDevices"
import RoleBasedGlobalAppScreen from "../Screens/GlobalAppInstallation/RoleBasedGlobalAppScreen"
import UsbEnabledDevices from "../Screens/UsbStatus/UsbEnabledDevices"
import UsbDisabledDevices from "../Screens/UsbStatus/UsbDisabledDevices"
import AllApps from "../Screens/AppManagement/AllApps/AllApps"
import AppBundles from "../Screens/AppManagement/AppBundles/AppBundles"
import AppRequests from "../Screens/AppManagement/AppRequests/AppRequests"
import UserAppInstallationRequest from "../Screens/AppManagement/UserAppInstallationRequest/UserAppInstallationRequest"
import AppBundlesForm from "../Screens/AppManagement/AppBundles/AppBundlesForm"
import GroupDevicesForm from "../Screens/Devices/GroupDevices/GroupDevicesForm"
import RemoteSupport from "../Screens/Devices/RemoteSupport/RemoteSupport"
import HelpDesk from "../Screens/HelpDesk/HelpDesk"
import RebootDevices from "../Screens/RebootDevices/RebootDevices"
import LeftOnDevices from "../Screens/Devices/LeftOnDevices/LeftOnDevices"
import WsusStatus from "../Screens/Devices/WsusStatus/WsusStatus"
import DevicePatches from "../Screens/Devices/DevicePatches/DevicePatches"
import Vulnerabilities from "../Screens/Devices/Vulnerabilities/Vulnerabilities"
import SchedulerForm from "../Screens/Devices/Scheduler/SchedulerForm"
import SecurityTabs from "../Screens/SecurityPolicies/SecurityTabs"
import UserLogs from "../Screens/Logs/UserLogs"
import CustomTask from "../Screens/Tasks/CustomTask"
import CustomTaskForm from "../Screens/Tasks/CustomTaskForm"
import PatchGroupDeviceList from "../Screens/Devices/WindowsPatchUpdate/PatchGroupDeviceList"
import UserActivity from "../Screens/UserActivity/UserActivity"
import SchedulerConfirmation from "../Screens/Devices/Scheduler/SchedulerConfirmation"
import BulkRegister from "../components/BulkRegister/BulkRegister"
import Roles from "../Screens/Roles/Roles"
import ManageRolePermissions from "../Screens/Roles/ManageRoles"
import Scripts from "../Screens/Tasks/Scripts"
import ScriptForm from "../Screens/Tasks/ScriptForm"
import InactivityNotificationSettings from "../Screens/Devices/InactivityNotificationSettings/InactivityNotificationSettings"
import DesktopBroadcasts from "../Screens/Broadcasts/DesktopBroadcasts"

const Dashboard = lazy(() => import("../Screens/Dashboard/Dashboard"))
const UserDashboard = lazy(() => import("../Screens/UserDashboard/UserDashboard"))
const Devices = lazy(() => import("../Screens/Devices/Devices"))
const DeviceTracker = lazy(() => import("../Screens/Devices/DeviceTracker/DeviceTracker"))
const DeviceManagement = lazy(() => import("../Screens/ITSM/DeviceManagement/DeviceManagement"))
const WindowsPatchUpdate = lazy(() => import("../Screens/Devices/WindowsPatchUpdate/WindowsPatchUpdate"))
const Scheduler = lazy(() => import("../Screens/Devices/Scheduler/Scheduler"))
const DeploymentStatus = lazy(() => import("../Screens/Devices/Scheduler/DeploymentStatus"))
const AgentHealth = lazy(() => import("../Screens/Devices/AgentHealth"))
const PerformanceHealth = lazy(() => import("../Screens/Devices/PerformanceHealth"))
const OsUpdates = lazy(() => import("../Screens/Devices/OsUpdates"))
const EncryptionKeys = lazy(() => import("../Screens/SecurityPolicies/EncryptionKeys"))
const Laps = lazy(() => import("../Screens/SecurityPolicies/Laps"))
const GlobalLogs = lazy(() => import("../Screens/Logs/GlobalLogs"))
const AppManagementForAllDevices = lazy(() => import("../Screens/AppManagement/AppManagementForAllDevices/AppManagementForAllDevices"))
const PendingTasks = lazy(() => import("../Screens/PendingTasks/PendingTasks"))
const SecurityPolicies = lazy(() => import("../Screens/SecurityPolicies/SecurityPolicies"))
const SecurityTemplates = lazy(() => import("../Screens/SecurityPolicies/SecurityTemplates"))
const CompliancePolicies = lazy(() => import("../Screens/CompliancePolicies/CompliancePolicies"))
const Users = lazy(() => import("../Screens/Users/Users"))
const UserLogins = lazy(() => import("../Screens/Users/UserLogins"))
const AllUpdates = lazy(() => import("../Screens/Devices/AllUpdates/AllUpdates"))
const PatchReports = lazy(() => import("../Screens/Devices/PatchReports/PatchReports"))
const BatteryReport = lazy(() => import("../Screens/Devices/BatteryReport/BatteryReport"))
const SoftwareReports = lazy(() => import("../Screens/Devices/SoftwareReports/SoftwareReports"))
const CatalogPolicy = lazy(() => import("../Screens/AppManagement/CatalogPolicy/CatalogPolicy"))
const AppControl = lazy(() => import("../Screens/AppManagement/AppControl/AppControl"))
const Settings = lazy(() => import("../Screens/Settings/Settings"))
const Maintenance = lazy(() => import("../Screens/Settings/Maintenance"))
const CorporateWallpaper = lazy(() =>
  import("../Screens/Settings/CorporateWallpaper")
)
const TimeTrackerPolicy = lazy(() =>
  import("../Screens/Settings/TimeTrackerPolicy")
)
const WireGuardPolicy = lazy(() =>
  import("../Screens/Settings/WireGuardPolicy")
)
const SecurityTraining = lazy(() =>
  import("../Screens/Training/SecurityTraining")
)
const TrainingCertificate = lazy(() =>
  import("../Screens/Training/TrainingCertificate")
)
const Assets = lazy(() => import("../Screens/Assets/Assets"))
const EnrollmentInvites = lazy(() => import("../Screens/Devices/EnrollmentInvites"))
const Assistant = lazy(() => import("../Screens/Assistant/Assistant"))
const Organizations = lazy(() => import("../Screens/Platform/Organizations"))

const routes = [
  {
    path: "/dashboard",
    component: <Dashboard />,
    access: "dashboard",
  },
  {
    path: "/assistant",
    component: <Assistant />,
    access: "dashboard",
  },
  {
    path: "/device-management/:id",
    component: <DeviceManagement />,
    access: "device-management/:id",
  },
  {
    path: "/patch-management/:id",
    component: <PatchManagement />,
    access: "patch-management/:id",
  },
  {
    path: "/app-management/:id",
    component: <AppManagement />,
    access: "app-management/:id",
  },
  {
    path: "/app-management",
    component: <AppManagementForAllDevices />,
    access: "app-management",
  },
  {
    path: "/online-devices",
    component: <OnlineDevices />,
    access: "online-devices",
  },
  {
    path: "/offline-devices",
    component: <OfflineDevices />,
    access: "offline-devices",
  },
  {
    path: "/inactivity-notification-settings",
    component: <InactivityNotificationSettings />,
    access: "inactivity-notification-settings",
  },
  {
    path: "/desktop-broadcasts",
    component: <DesktopBroadcasts />,
    access: [
      "broadcasts",
      "create-broadcasts",
      "maintenance",
      "inactivity-notification-settings",
      "schedules",
    ],
  },
  {
    path: "/devices",
    component: <Devices />,
    access: ["devices", "u-devices"],
  },
  {
    path: "/assets",
    component: <Assets />,
    access: ["devices", "u-devices"],
  },
  {
    path: "/device-tracker",
    component: <DeviceTracker />,
    access: ["devices", "u-devices"],
  },
  {
    path: "/device-enrollment",
    component: <EnrollmentInvites />,
    access: "device-enrollment",
  },
  {
    path: "/patches",
    component: <WindowsPatchUpdate />,
    access: "patches",
  },
  {
    path: "/group-devices",
    component: <GroupDevices />,
    access: ["group-devices", "u-group-devices"],
  },
  {
    path: "/add/group-devices",
    component: <GroupDevicesForm />,
    access: "add/group-devices",
  },
  {
    path: "/edit/group-devices/:id",
    component: <GroupDevicesForm />,
    access: "edit/group-devices/:id",
  },
  {
    path: "/group-devices-lists/:id",
    component: <GroupDevicesList />,
    access: "group-devices-lists/:id",
  },
  {
    path: "/global-patches",
    component: <AllUpdates />,
    access: "global-patches",
  },
  {
    path: "/global-patches/:id",
    component: <GlobalPatchManagement />,
    access: "global-patches/:id",
  },
  {
    path: "/patch-reports",
    component: <PatchReports />,
    access: "patch-reports",
  },
  {
    path: "/software-reports",
    component: <SoftwareReports />,
    access: ["software-reports", "patch-reports"],
  },
  {
    path: "/all-softwares",
    component: <AllUpdates />,
    access: "all-softwares",
  },
  {
    path: "/all-softwares/:id",
    component: <GlobalSoftwareManagement />,
    access: "all-softwares/:id",
  },
  {
    path: "/deleted-devices",
    component: <DeletedDevices />,
    access: "deleted-devices",
  },
  {
    path: "/all-logs",
    component: <GlobalLogs />,
    access: "all-logs",
  },
  {
    path: "/logs/:hostname/:type",
    component: <ViewLogs />,
    access: "logs/:hostname/:type",
  },
  {
    path: "/logs/:hostname",
    component: <UserLogs />,
    access: "logs/:hostname",
  },
  {
    path: "/compliant-devices",
    component: <CompliantDevices />,
    access: "compliant-devices",
  },
  {
    path: "/non-compliant-devices",
    component: <NonCompliantDevices />,
    access: "non-compliant-devices",
  },
  // {
  //   path: "/global-app-installation",
  //   component: <RoleBasedGlobalAppScreen />,
  //   allowedRoles: ["admin", "user"],
  // },
  {
    path: "/users",
    component: <Users />,
    access: "users",
  },
  {
    path: "/user-logins",
    component: <UserLogins />,
    access: ["users", "users-activity"],
  },
  {
    path: "/settings",
    component: <Settings />,
    access: "settings",
  },
  {
    path: "/deleted-devices",
    component: <DeletedDevices />,
    access: "deleted-devices",
  },
  {
    path: "/usb-enabled-devices",
    component: <UsbEnabledDevices />,
    access: "usb-enabled-devices",
  },
  {
    path: "/usb-disabled-devices",
    component: <UsbDisabledDevices />,
    access: "usb-disabled-devices",
  },
  {
    path: "/all-apps",
    component: <AllApps />,
    access: "all-apps",
  },
  {
    path: "/app-bundles",
    component: <AppBundles />,
    access: "app-bundles",
  },
  {
    path: "/add-app-bundles",
    component: <AppBundlesForm />,
    access: "add-app-bundles",
  },
  {
    path: "/edit-app-bundles/:id",
    component: <AppBundlesForm />,
    access: "edit-app-bundles/:id",
  },
  {
    path: "/app-requests",
    component: <AppRequests />,
    access: "app-requests",
  },
  {
    path: "/catalog-policy",
    component: <CatalogPolicy />,
    access: ["catalog-policy", "app-requests"],
  },
  {
    path: "/app-control",
    component: <AppControl />,
    access: ["app-control", "catalog-policy", "app-requests", "all-apps"],
  },
  {
    path: "/user-app_installation-request",
    component: <UserAppInstallationRequest />,
    access: "user-app_installation-request",
  },
  {
    path: "/remote-support",
    component: <RemoteSupport />,
    access: "remote-support",
  },
  {
    path: "/help-desk",
    component: <HelpDesk />,
    access: "remote-support",
  },
  {
    path: "/away-devices",
    component: <AwayDevices />,
    access: "away-devices",
  },
  {
    path: "/reboot-devices",
    component: <RebootDevices />,
    access: "reboot-devices",
  },
  {
    path: "/left-on-devices",
    component: <LeftOnDevices />,
    access: ["devices", "u-devices"],
  },
  {
    path: "/wsus-status",
    component: <WsusStatus />,
    access: "patches",
  },
  {
    path: "/device-patches",
    component: <DevicePatches />,
    access: "patches",
  },
  {
    path: "/vulnerabilities",
    component: <Vulnerabilities />,
    access: "patches",
  },
  {
    path: "/add-schedule",
    component: <SchedulerForm />,
    access: "add-schedule",
  },
  {
    path: "/edit-schedule/:id",
    component: <SchedulerForm />,
    access: "edit-schedule/:id",
  },
  {
    path: "/confirmation-update-details/:task?",
    component: <SchedulerConfirmation />,
    access: "confirmation-update-details/:task?",
  },
  {
    path: "/schedules",
    component: <Scheduler />,
    access: "schedules",
  },
  {
    path: "/deployments",
    component: <DeploymentStatus />,
    access: "schedules",
  },
  {
    path: "/agent-health",
    component: <AgentHealth />,
    access: ["devices", "u-devices"],
  },
  {
    path: "/performance-health",
    component: <PerformanceHealth />,
    access: ["devices", "u-devices"],
  },
  {
    path: "/battery-report",
    component: <BatteryReport />,
    access: ["devices", "u-devices"],
  },
  {
    path: "/os-updates",
    component: <OsUpdates />,
    access: "patches",
  },
  {
    path: "/encryption-keys",
    component: <EncryptionKeys />,
    access: "security-policies",
  },
  {
    path: "/laps",
    component: <Laps />,
    access: "security-policies",
  },
  {
    path: "/maintenance",
    component: <Maintenance />,
    access: "maintenance",
  },
  {
    path: "/corporate-wallpaper",
    component: <CorporateWallpaper />,
    access: "broadcasts",
  },
  {
    path: "/time-tracker-policy",
    component: <TimeTrackerPolicy />,
    access: "broadcasts",
  },
  {
    path: "/wireguard-policy",
    component: <WireGuardPolicy />,
    access: "broadcasts",
  },
  {
    path: "/training",
    component: <SecurityTraining />,
    access: [
      "u-devices",
      "my-profile",
      "devices",
      "broadcasts",
      "create-broadcasts",
    ],
  },
  {
    path: "/training/certificate/:id",
    component: <TrainingCertificate />,
    access: [
      "u-devices",
      "my-profile",
      "devices",
      "broadcasts",
      "create-broadcasts",
    ],
  },
  {
    path: "/my-profile",
    component: <UserDashboard />,
    access: "my-profile",
  },
  {
    path: "/clear-tasks",
    component: <PendingTasks />,
    access: "clear-tasks",
  },
  {
    path: "/security-policies",
    component: <SecurityPolicies />,
    access: "security-policies",
  },
  {
    path: "/security-templates",
    component: <SecurityTemplates />,
    access: "security-templates",
  },
  {
    path: "/compliance-policies",
    component: <CompliancePolicies />,
    access: ["compliance-policies", "security-templates"],
  },
  {
    path: "/security-policies/:tab/:hostname",
    component: <SecurityTabs />,
    access: "security-policies/:tab/:hostname",
  },
  {
    path: "/custom-task",
    component: <CustomTask />,
    access: "custom-task",
  },
  {
    path: "/add-customtask",
    component: <CustomTaskForm />,
    access: "add-customtask",
  },
  {
    path: "/edit-customtask/:id",
    component: <CustomTaskForm />,
    access: "edit-customtask/:id",
  },
  {
    path: "/patch-group-devices-lists/:id",
    component: <PatchGroupDeviceList />,
    access: "patch-group-devices-lists/:id",
  },
  {
    path: "/users-activity",
    component: <UserActivity />,
    access: "users-activity",
  },
  {
    path: "/bulk-register",
    component: <BulkRegister />,
    access: "bulk-register",
  },
  {
    path: "/platform/organizations",
    component: <Organizations />,
    access: "platform",
  },
  {
    path: "/roles",
    component: <Roles />,
    access: "roles",
  },
  {
    path: "/roles/:id",
    component: <ManageRolePermissions />,
    access: "roles/:id",
  },
  {
    path: "/scripts",
    component: <Scripts />,
    access: "scripts",
  },
  {
    path: "/add-script",
    component: <ScriptForm />,
    access: "add-script",
  },
  {
    path: "/edit-script/:id",
    component: <ScriptForm />,
    access: "edit-script/:id",
  },
]

const InnerRoutes = ({ isAuthenticated }) => {
  const userInfo = useSelector((state) => state.user.data)
  return (
    <Layout>
      <Suspense fallback={<PageSkeleton cards={4} rows={6} />}>
      <Routes>
        <Route path="/">
          {userInfo?.role === "network-admin" ? (
            <Route index element={<Dashboard />} />
          ) : (
            <Route index element={<UserDashboard />} />
          )}
          {routes.map(({ path, component, access }, id) => (
            <Route
              key={id}
              path={path}
              element={
                <ProtectedRoute
                  isAuthenticated={isAuthenticated}
                  access={access}
                >
                  {component}
                </ProtectedRoute>
              }
            />
          ))}
        </Route>
        <Route path="*" element={<PageNotFound />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
      </Routes>
      </Suspense>
    </Layout>
  )
}

export default InnerRoutes
